import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { css, styled } from '@mui/material/styles';
import tinycolor from 'tinycolor2';

//Redux
import { selectLegibleElementColor } from 'reducers/config';
import { selectIsMobile } from 'reducers/main';
import { useUpdateConversation } from 'components/App/hooks';

//Components
import FormWrapper from '../FormWrapper/FormWrapper';
import UserMessage from '../UserMessage';

//Types
import type { ZendeskAttachmentUploadStepComponent } from 'api/types';

//Helpers
import { onChangeHandler } from './ZendeskAttachmentUploadStepHelpers';

//Assets
import AttachIcnImg from '../../assets/icons/attach-icon.svg';
import DeleteIcn from '../../assets/icons/remove-icon.svg';
import WhiteAttachIcn from '../../assets/icons/white-attach-icon.svg';
import { useTheme } from '@mui/material/styles';
import OptionButton from 'components/OptionButton';
import { startNewIntentSelection } from 'actions/workflows-v2/workflowsV2';
import { apiUploadZendeskAttachment } from 'api/requests';
import {
  selectIsUploadingAttachment,
  setIsUploadingAttachment,
  setDismissableNotification,
  selectFeatureFlags,
} from 'reducers/workflows';
import { CustomFetchError } from 'api/customFetch';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import AttachmentUploadStep from 'components/AttachmentUploadStep';
import { Box } from '@mui/material';

interface ZendeskAttachmentUploadStepProps {
  isLastStep: boolean;
  setStepFileNames: (fileNames?: string[]) => void;
  stepIndex: number;
  widgetComponent: ZendeskAttachmentUploadStepComponent;
}

const ZendeskAttachmentUploadStep: React.FC<
  React.PropsWithChildren<ZendeskAttachmentUploadStepProps>
> = ({ isLastStep, setStepFileNames, stepIndex, widgetComponent }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const updateConversation = useUpdateConversation();
  const {
    forethought_text: componentText,
    previous_attachment_tokens: previousAttachmentTokens,
    required,
    should_use_s3: shouldUseS3 = false,
    upload_url: uploadURL,
  } = widgetComponent.component_fields;
  const inputReference = useRef<HTMLInputElement>(null);
  const isMobile = useAppSelector(selectIsMobile);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isUploadingAttachment = useAppSelector(selectIsUploadingAttachment);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const [files, setFiles] = useState<Array<File> | null>(null);
  const [attachmentTokens, setAttachmentTokens] = useState<string>('');
  const areFilesSent = widgetComponent.isSent;
  const { isReroutedByNegativeFeedback } = widgetComponent;
  const fileNames = widgetComponent.file_names || [];
  const neverMindSelection = widgetComponent?.neverMindSelection || '';
  const isNeverMindButtonSelected = neverMindSelection.length > 0;
  const shouldHideNeverMindButton = featureFlags.includes(
    'hide_never_mind_button',
  );

  useEffect(() => {
    if (shouldUseS3) {
      return;
    }
    const attachmentTokensLength = attachmentTokens.split(',').length;
    if (files?.length === attachmentTokensLength) {
      // If there already are uploaded files, keep them in the sys CV value
      if (previousAttachmentTokens) {
        updateConversation({
          zendesk_attachment_tokens:
            attachmentTokens + ',' + previousAttachmentTokens,
        });
      } else {
        updateConversation({ zendesk_attachment_tokens: attachmentTokens });
      }
      setStepFileNames(files.map(file => file.name));
    }
  }, [attachmentTokens, shouldUseS3]);

  if (shouldUseS3) {
    return (
      <AttachmentUploadStep
        isLastStep={isLastStep}
        stepIndex={stepIndex}
        widgetComponent={{
          ...widgetComponent,
          component_fields: {
            ...widgetComponent.component_fields,
            max_individual_size: null,
            max_total_size: 20,
          },
        }}
      />
    );
  }

  const handleOnSubmit = async () => {
    dispatch(setIsUploadingAttachment(true));
    dispatch(setDismissableNotification(''));
    const trackingData = { failedReason: '', successfulUploads: 0 };

    try {
      await Promise.all(
        (files || []).map(async (file: File) => {
          await apiUploadZendeskAttachment(uploadURL, file).then(token => {
            setAttachmentTokens(attachmentTokens =>
              attachmentTokens ? `${attachmentTokens},${token}` : token,
            );
            trackingData.successfulUploads++;
          });
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        if (error instanceof CustomFetchError) {
          if (error.status === 413) {
            dispatch(
              setDismissableNotification(
                'Upload error: file is too big, limit is 50 MB',
              ),
            );

            trackingData.failedReason = 'File too large';
            return;
          }
        }

        trackingData.failedReason = error.message;

        dispatch(setDismissableNotification('Upload error. Please try again.'));
      }
    } finally {
      dispatch(setIsUploadingAttachment(false));
      dispatch(
        sendTrackingEvent({
          ...trackingData,
          event_type: 'widget-zendesk-attachment-upload',
        }),
      );
    }
  };

  // Display the list of sent files as a message
  if (areFilesSent || isReroutedByNegativeFeedback) {
    return (
      <UserMessage stepIndex={stepIndex}>
        {isNeverMindButtonSelected ? (
          neverMindSelection
        ) : (
          <>
            {fileNames.length > 0 && (
              <SentFilesContainer>
                {fileNames?.map((fileName: string, index: number) => (
                  <SentFile
                    isLastItem={index === fileNames.length - 1}
                    key={fileName}
                  >
                    <WhiteAttachIcon alt='' src={WhiteAttachIcn} />
                    <span>{fileName}</span>
                  </SentFile>
                ))}
              </SentFilesContainer>
            )}
            {fileNames.length === 0 && (
              <span>{componentText.skip_attachment}</span>
            )}
          </>
        )}
      </UserMessage>
    );
  }
  // Display the form to upload a file
  else
    return (
      <>
        <FormWrapper
          disableSubmitOnEnter
          formTitle={
            required
              ? componentText.form_title_required
              : componentText.form_title
          }
          isDisabled={!files || files?.length === 0 || isUploadingAttachment}
          isLastStep={isLastStep}
          onSubmit={(e: React.SyntheticEvent) => {
            e.preventDefault();
            handleOnSubmit();
          }}
          stepIndex={stepIndex}
          submitButtonText={
            isUploadingAttachment ? 'Uploading...' : componentText.submit_button
          }
        >
          <AddFileBox color={theme.palette.colors.grey[600]}>
            <Message>
              <AddFileLabel
                backgroundColor={theme.palette.colors.white}
                disabled={isUploadingAttachment}
                textColor={
                  isUploadingAttachment
                    ? theme.palette.colors.grey[400]
                    : legibleElementColor
                }
              >
                {componentText.add_file}
                <input
                  className='visually-hidden'
                  disabled={isUploadingAttachment}
                  onChange={() => {
                    onChangeHandler(inputReference, setFiles);
                  }}
                  ref={inputReference}
                  type='file'
                />
              </AddFileLabel>
              {!required && (
                <>
                  or
                  <SkipUploadButton
                    backgroundColor={theme.palette.colors.white}
                    color={
                      isUploadingAttachment
                        ? theme.palette.colors.grey[400]
                        : theme.palette.colors.grey[600]
                    }
                    disabled={isUploadingAttachment}
                    onClick={() => {
                      // Only set zendesk_attachment_tokens if they are not set yet,
                      // otherwise we may owerwrite already uploaded attachments
                      if (previousAttachmentTokens) {
                        updateConversation({});
                      } else {
                        updateConversation({
                          zendesk_attachment_tokens: '',
                        });
                      }
                      setFiles(null);
                      setStepFileNames(files?.map(file => file.name));
                    }}
                  >
                    {componentText.skip_attachment}
                  </SkipUploadButton>
                </>
              )}
            </Message>
          </AddFileBox>

          <>
            {files?.map((file: File, fileIndex: number) => {
              return (
                <FileContainer
                  backgroundColor={tinycolor(legibleElementColor) + '14'}
                  key={fileIndex}
                  textColor={legibleElementColor}
                >
                  <AttachIcon alt='' src={AttachIcnImg} />
                  <FileName
                    isMobile={isMobile}
                    textColor={theme.palette.text.primary}
                  >
                    {file.name}
                  </FileName>
                  <DeleteFileIcon
                    aria-label={`Delete file ${file.name}`}
                    onClick={() => {
                      const newFiles =
                        files &&
                        files.filter(
                          (file: File, index: number) => fileIndex !== index,
                        );
                      setFiles(newFiles);
                    }}
                    type='button'
                  />
                </FileContainer>
              );
            })}
          </>
        </FormWrapper>
        {!shouldHideNeverMindButton && (
          <Box sx={{ paddingX: '8px' }}>
            <OptionButton
              margin='0 auto 16px 0'
              onClick={() => {
                dispatch(
                  startNewIntentSelection(
                    widgetComponent.component_fields.forethought_text
                      .never_mind,
                  ),
                );
              }}
              text={
                widgetComponent.component_fields.forethought_text.never_mind
              }
            />
          </Box>
        )}
      </>
    );
};

export default ZendeskAttachmentUploadStep;

const AddFileBox = styled('div')<{ color: string }>`
  height: 80px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: ${props => props.color};
  margin-bottom: 10px;
`;

const Message = styled('div')`
  display: flex;
  justify-content: center;
  gap: 5px;
`;

const AddFileLabel = styled('label')<{
  backgroundColor: string;
  disabled: boolean;
  textColor: string;
}>`
  border: none;
  outline: none;
  color: ${props => props.textColor};
  background: ${props => props.backgroundColor};
  cursor: pointer;
  text-decoration: underline;

  :focus-within {
    outline: 2px solid var(--legible-element-color);
  }

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}
`;

const SkipUploadButton = styled('button')<{
  backgroundColor: string;
  color: string;
  disabled: boolean;
}>`
  border: none;
  outline: none;
  text-decoration: underline;
  display: block;
  background: ${props => props.backgroundColor};
  color: ${props => props.color};
  cursor: pointer;
  padding: 0;

  :focus-within {
    outline: 2px solid var(--legible-element-color);
  }

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}
`;

const FileContainer = styled('div')<{
  backgroundColor: string;
  textColor: string;
}>`
  display: flex;
  padding: 8px;
  background: ${props => props.backgroundColor};
  border-radius: 5px;
  color: ${props => props.textColor};
  margin-bottom: 5px;
  align-items: center;
  position: relative;
`;

const AttachIcon = styled('img')`
  margin-right: 10px;
`;

const FileName = styled('div')<{
  isMobile: boolean | undefined;
  textColor: string;
}>`
  color: #404b5a;
  width: ${props => (props.isMobile ? '180' : '250')}px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const DeleteFileIcon = styled('button')`
  position: absolute;
  cursor: pointer;
  right: 10px;
  width: 18px;
  height: 18px;
  background-image: url(${DeleteIcn});
  background-size: contain;
  border: none;
`;

const SentFilesContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const SentFile = styled('div')<{ isLastItem: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: ${props => (props.isLastItem ? '0' : '5')}px;
`;

const WhiteAttachIcon = styled('img')`
  margin-right: 5px;
`;
